import styled from '@emotion/styled';
import React from 'react';
import {mixinSVGIcon, mixinSVGIconTiny} from 'webapp-lib/styling';

import {Props} from '.';

export const TextItem = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

function IconComponent<U extends keyof HTMLElementTagNameMap = 'span'>({
  baseElement = 'span',
  ...props
}: {
  baseElement?: keyof HTMLElementTagNameMap;
} & React.PropsWithChildren<
  React.HTMLAttributes<HTMLElementTagNameMap[U]>
>): React.ReactElement {
  return React.createElement(baseElement, props);
}

export const StyledIcon = styled(IconComponent)`
  display: flex;

  svg {
    ${mixinSVGIcon}
    margin-right: var(--gutter-little);

    :not(:first-of-type) {
      margin-left: 0;
    }
  }
`;

export const TextContainer = styled.div`
  width: 100%;
`;

export const StyledExpandIcon = styled.span`
  display: flex;
  align-items: center;
  margin-left: auto;
  margin-right: calc(var(--gutter-tiny) * -1);

  svg {
    ${mixinSVGIconTiny}
    fill: var(--color-core-link);
  }
`;

function determineColor({
  activated,
  item: {inactive = false, inactiveStyle = false},
}: Props): string {
  return inactive || inactiveStyle
    ? 'var(--list-inactive-item-color)'
    : activated
      ? 'var(--activated-item-text-color)'
      : 'var(--color-core-link)';
}

export const StyledListItem = styled('div', {
  shouldForwardProp: (prop: string) =>
    ![
      'sectioned',
      'inactive',
      'inactiveStyle',
      'showSelectionCheckbox',
    ].includes(prop),
})<Props>`
  color: ${determineColor};
  > ${StyledIcon}>svg {
    fill: ${determineColor};
  }
  /* Here we set that the background-color for activated or dragged
  items should be more important than previously set item colors*/

  & {
    ${({activated, isDraggingOver}): string =>
      activated || isDraggingOver
        ? `background-color: var(--color-state-active);`
        : ''}
  }
  ${({item: {inactive = false}}): string =>
    inactive ? 'cursor: not-allowed;' : ''}
  &.drag-image {
    color: ${({item: {inactive = false, inactiveStyle = false}}): string =>
      inactive || inactiveStyle
        ? 'var(--list-inactive-item-color)'
        : 'var(--color-core-link)'};
  }
  height: 32px;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding: 5px var(--gutter);
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-thin);
  overflow: hidden;
  text-overflow: ellipsis;

  ${TextContainer} {
    margin-left: ${({item: {Icon}, sectioned}): string =>
      Icon ? '0' : sectioned ? 'var(--gutter)' : '0'};
  }

  :hover {
    ${
      ({item: {inactive}}): string =>
        /* eslint-disable @typescript-eslint/restrict-template-expressions */
        inactive
          ? ''
          : `background-color: var(--color-state-hover);
       color: var(--activated-item-text-color);

       >${StyledIcon}>svg {
         fill: var(--activated-item-text-color);
       }`
      /* eslint-enable @typescript-eslint/restrict-template-expressions */
    }
  }

  > ${StyledExpandIcon}>svg {
    fill: ${determineColor};
  }
`;
